import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { useButtonState } from '../hooks/useButtonState';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiLogIn, FiUserPlus, FiAlertCircle, FiCheck } = FiIcons;

const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const { signIn, signUp } = useAuth();
  const { success: showSuccess } = useToast();
  const navigate = useNavigate();

  const {
    state: authState,
    error: authError,
    handleAction: handleAuthAction,
    reset: resetAuthState,
    icon: authIcon,
    text: authText,
    classes: authClasses,
  } = useButtonState({
    idleText: isLogin ? 'Sign In' : 'Create Account',
    loadingText: 'Processing...',
    successText: isLogin ? 'Signed In!' : 'Created!',
    failedText: 'Failed',
    idleIcon: isLogin ? FiLogIn : FiUserPlus,
    onSuccess: () => {
      showSuccess(`${isLogin ? 'Sign in' : 'Account created'} successful! Redirecting...`);
      setTimeout(() => navigate('/'), 1000);
    },
    onError: (err) => {
      setError(err?.message || 'Authentication failed. Please try again.');
    },
    autoResetDelay: 2000,
  });

  const handleSwitchMode = (nextIsLogin) => {
    setIsLogin(nextIsLogin);
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    await handleAuthAction(async () => {
      if (isLogin) {
        const { error } = await signIn(email, password);
        if (error) throw error;
      } else {
        const { error } = await signUp(email, password);
        if (error) throw error;
      }
    });
  };

  return (
    <div className="min-h-[calc(100vh-100px)] flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-gray-50">
      <div className="max-w-md w-full space-y-8">
        <div className="neo-card p-0 overflow-hidden shadow-neo-xl">
          {/* Header Tabs */}
          <div className="flex border-b-2 border-black">
            <button
              onClick={() => handleSwitchMode(true)}
              className={`flex-1 py-4 text-center font-bold uppercase transition-colors min-h-[56px] ${
                isLogin ? 'bg-neo-yellow' : 'bg-gray-100 hover:bg-gray-200'
              }`}
            >
              Sign In
            </button>
            <div className="w-0.5 bg-black"></div>
            <button
              onClick={() => handleSwitchMode(false)}
              className={`flex-1 py-4 text-center font-bold uppercase transition-colors min-h-[56px] ${
                !isLogin ? 'bg-neo-blue' : 'bg-gray-100 hover:bg-gray-200'
              }`}
            >
              Sign Up
            </button>
          </div>

          {/* Form Body */}
          <div className="p-8 bg-white">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-black text-white mb-4 border-2 border-black shadow-[4px_4px_0px_0px_#86EFAC]">
                <SafeIcon icon={isLogin ? FiLogIn : FiUserPlus} size={28} />
              </div>
              <h2 className="text-2xl font-black uppercase">
                {isLogin ? 'Welcome Back' : 'Create Account'}
              </h2>
              <p className="mt-2 text-sm text-gray-600 font-mono">
                {isLogin
                  ? 'Access your personal prompt library.'
                  : 'Start organizing your AI workflow.'}
               </p>
             </div>


             <form className="space-y-6" onSubmit={handleSubmit}>
              {error && (
                <div className="bg-red-100 border-2 border-red-500 text-red-700 p-3 flex items-start text-sm font-bold">
                  <SafeIcon icon={FiAlertCircle} className="mr-2 mt-0.5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

               <div className="space-y-4">
                 <div>
                   <label htmlFor="email" className="block text-xs font-bold uppercase mb-1">
                     Email Address
                   </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="neo-input input-touch"
                      placeholder="name@example.com"
                    />
                  </div>
                 <div>
                   <label htmlFor="password" className="block text-xs font-bold uppercase mb-1">
                     Password
                   </label>
                   <input
                     id="password"
                     name="password"
                     type="password"
                     required
                     value={password}
                     onChange={(e) => setPassword(e.target.value)}
                     className="neo-input input-touch"
                     placeholder="•••••••"
                   />
                 </div>
               </div>
                <div>
                  <button
                    type="submit"
                    disabled={authState === 'loading'}
                    className={`w-full flex justify-center items-center py-3 px-4 border-2 border-black shadow-neo text-sm font-bold uppercase tracking-wider text-black focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-black transition-all active:shadow-none active:translate-x-[2px] active:translate-y-[2px] ${authClasses} ${
                      isLogin ? 'bg-neo-yellow hover:bg-yellow-300' : 'bg-neo-blue hover:bg-blue-300'
                    }`}
                  >
                    {authIcon && <SafeIcon icon={authIcon} className="mr-2" size={16} />}
                    <span>{authText}</span>
                  </button>
                </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
